import React, { useId, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { formatCurrency } from "../../../../util";
import { cn } from "lib/utils";
import { useTaxStatement } from "../../../../util/taxStatement";
import { STATION_TIME_ZONE } from "util/stationTime";
import { giftVoucherValidUntilYear } from "util/giftVoucherValidity";

const TicketHole = ({ side, uniqueId }: { side: "left" | "right"; uniqueId: string }) => {
  const clipPathId = `${side}Hole-${uniqueId}`;
  const isRight = side === "right";

  return (
    <svg
      className={cn(
        "absolute top-[29%] w-4 h-4 z-10 pointer-events-none",
        isRight ? "right-[-8px]" : "left-[-8px]"
      )}
      viewBox="0 0 16 16"
    >
      <defs>
        <clipPath id={clipPathId}>
          <rect x={isRight ? "0" : "8"} y="0" width="8" height="16" />
        </clipPath>
      </defs>
      <rect x={isRight ? "5" : "7"} y="0" width="4" height="16" fill="white" />
      <circle cx="8" cy="8" r="8" fill="rgb(249, 250, 251)" clipPath={`url(#${clipPathId})`} />
      <path
        d={isRight ? "M 8 0.5 A 7.5 7.5 0 0 0 8 15.5" : "M 8 0.5 A 7.5 7.5 0 0 1 8 15.5"}
        stroke="rgba(0, 0, 0, 0.1)"
        strokeWidth="1"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
};

function VoucherBookingSummaryStep() {
  const { t, i18n } = useTranslation();
  const taxStatement = useTaxStatement();
  const uniqueId = useId();
  const { voucherAmount, personalData } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  // The date the backend will stamp on the voucher (giftVoucherValidUntil):
  // 31 December of the third year after the year of purchase.
  const validityDate = useMemo(() => {
    const date = new Date(Date.UTC(giftVoucherValidUntilYear(new Date()), 11, 31, 12));
    return date.toLocaleDateString(i18n.language === "de" ? "de-DE" : i18n.language, {
      timeZone: STATION_TIME_ZONE,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [i18n.language]);

  const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em]";

  return (
    <div className="relative overflow-clip">
      <TicketHole side="left" uniqueId={uniqueId} />
      <TicketHole side="right" uniqueId={uniqueId} />

      <div className="bg-white flex flex-col py-8 px-5 border border-foreground/10 rounded-md text-foreground">
        <div>
          <p>{t("general.voucher_purchase.summary.title")}</p>
          <p className={textBoldClass}>
            {formatCurrency(voucherAmount, i18n.language)}
          </p>
        </div>

        <div className="mt-2 pb-3 mb-3 border-b border-foreground/10">
          <div className="flex w-full flex-row justify-between">
            <span className={textBoldClass}>
              {t("general.voucher_purchase.summary.validity")}
            </span>
          </div>
          <p className="text-sm">{validityDate}</p>
        </div>

        <div className="pb-3 mb-3 border-b border-foreground/10">
          <div className="flex w-full flex-row justify-between">
            <span className={textBoldClass}>
              {t("general.voucher_purchase.summary.sent_to")}
            </span>
          </div>
          <p className="text-sm">{personalData.email}</p>
        </div>

        <div className="flex flex-row justify-between">
          <p>{t("general.voucher_purchase.summary.total")}</p>
          <p className={textBoldClass}>
            {formatCurrency(voucherAmount, i18n.language)}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {taxStatement.isSmallBusiness
            ? taxStatement.sentence
            : t("general.voucher_purchase.summary.vatNote")}
        </p>

        <div className="bg-[#EDF0F5] text-gray-800 p-3 rounded-lg mt-3">
          <p className="text-sm">
            <Trans
              i18nKey={"general.voucher_purchase.summary.note"}
              components={{
                bold: <span className="font-bold"></span>,
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
}

export default VoucherBookingSummaryStep;
